import React, { useState } from "react"

import { Form, Formik } from "formik"
import * as Yup from "yup"

import {
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline"

import { adminClient as client } from "src/urql-client"

import { gql, useMutation } from "hooks/urql"

import {
  FormFailures,
  handleFailure,
} from "components/Forms/Formik/hookComponents"
import ModalWithProvidedBody, { Footer } from "components/ModalWithProvidedBody"

import FormFields from "./FormFields"

const ValidationSchema = Yup.object().shape({
  firstName: Yup.string().required("First name required"),
})

export type NewReferralProps = {
  afterClose: VoidFunction
}

const NewReferral: React.FC<NewReferralProps> = ({ afterClose }) => {
  const [link, setLink] = useState<null | string>()
  const [copied, setCopied] = useState(false)

  const formState = {
    userType: "parent",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    gradeId: undefined,
  }

  const addToClipboard = (linkText: string) => {
    navigator.clipboard.writeText(linkText)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const [, runMutation]: any[] = useMutation(createReferralMutation, client)

  const handleSubmit = (values, actions) => {
    runMutation({ input: values })
      .then(
        result => {
          const { failures, referralLink } = result.data.createReferral
          if (failures.length > 0) {
            handleFailure(actions, failures)
          } else {
            setLink(referralLink)
            addToClipboard(referralLink)
            actions.setSubmitting(false)
            afterClose()
          }
        },
        () => handleFailure(actions)
      )
      .catch(() => handleFailure(actions))
  }

  return (
    <>
      <ModalWithProvidedBody
        buttonText="New Referral"
        buttonClassName="btn btn-info"
        modalTitle="New Referral"
      >
        {({ closeModal }) => (
          <>
            <Formik
              initialValues={formState}
              onSubmit={handleSubmit}
              validationSchema={ValidationSchema}
              validateOnBlur={false}
              validateOnChange={false}
            >
              {({ values }) => (
                <Form>
                  <div className="modal-body">
                    {link ? (
                      <>
                        <div className="text-lg">
                          Send this link to {values.firstName} in order to give
                          them a head start on registration!
                        </div>
                        <div className="mt-4">
                          <div className="italic">
                            {copied ? "Link is copied!" : "Click to re-copy"}
                          </div>
                          <div
                            className="overflow-hide flex w-fit cursor-pointer items-center space-x-2 rounded-md bg-gray-50 py-1 px-2 ring-1 ring-inset ring-gray-500"
                            onClick={() => addToClipboard(link)}
                          >
                            <button
                              type="button"
                              className="cursor-pointer border-none bg-transparent focus:outline-none"
                            >
                              {copied ? (
                                <ClipboardDocumentCheckIcon className="h-8 w-8 text-green-500" />
                              ) : (
                                <ClipboardDocumentIcon className="h-8 w-8 text-gray-400" />
                              )}
                            </button>
                            <span className="break-all font-mono">{link}</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <FormFields newRecord />
                    )}
                  </div>

                  <div className="modal-footer flex-col">
                    <FormFailures />
                    {link ? (
                      <button
                        onClick={() => {
                          closeModal()
                          setLink(null)
                        }}
                        className="btn btn-success"
                        type="button"
                      >
                        Done
                      </button>
                    ) : (
                      <Footer cancelAction={closeModal} />
                    )}
                  </div>
                </Form>
              )}
            </Formik>
          </>
        )}
      </ModalWithProvidedBody>
    </>
  )
}

const createReferralMutation = gql`
  mutation CreateReferral($input: ReferralInputObject!) {
    createReferral(input: $input) {
      referralLink
      failures {
        message
      }
    }
  }
`

export default NewReferral
